import { makeAutoObservable } from "mobx";
import { Transaction } from "@/types/Transactions";
import {
  combineDateAndTime,
  DEFAULT_TIME,
  isAfter,
  nextAvailableTime,
  toDate,
  toLocalDateISOString,
  toLocalTime,
} from "@/utils/Dates";
import { ExtendedShareholderDTO } from "@/types/Shareholder";
import { FilterKeys, filterKeyLabels, TransactionType } from "@/types/enums/TransactionType";
import { compact, groupBy, identity, mapValues, max, min } from "lodash";

import { TMessage } from "@/i18n/tt";
import { useShareholdersStore, useTranslation } from "@/stores/RootStoreContext";
import { useMemo, useState } from "react";
import { filterKeysMap } from "@/types/const/TransactionType";
import { LocalDateFieldModel, RadioFieldModel, TimeFieldModel } from "@/common/commonForms";
import { ShareTypeCode } from "@/common/share";
import { DropdownMenuItemType } from "@/components-v2/Dropdown";
import { isNotRegisteredIssue, supportsEdit } from "./transactionLogDomain";
import { isInitialTransactionType } from "@/types/const/Transactions";
import { DCEvent, navigate } from "@/events/eventsDomain";
import { adminEditShareholdersTransaction, adminProject } from "@/utils/navigation";
import { UploadedAttachment } from "@/fileUpload/fileUploadDomain";
import { AttachmentType } from "@/dbot/common/commonDomain";

export class TransactionDateAndTimeForm {
  transactionDate: LocalDateFieldModel;
  transactionTime = new TimeFieldModel({ required: true });
  warning?: TMessage;

  constructor(
    readonly minDate: string,
    readonly options?: {
      value?: string;
      onChange?: (params: { date: string | undefined; time: string | undefined }) => void;
    },
    readonly maxDate?: string,
  ) {
    const onChange = () => {
      this.checkFutureTransaction();
      return options?.onChange?.({
        date: this.transactionDate.validatedValue,
        time: this.transactionTime.validatedValue,
      });
    };

    this.transactionDate = new LocalDateFieldModel({
      required: true,
      minDate: minDate,
      maxDate: maxDate,
      onChange,
      value: options?.value ? toLocalDateISOString(options?.value) : undefined,
    });
    this.transactionTime = new TimeFieldModel({
      required: true,
      onChange,

      value: options?.value ? toLocalTime(options?.value) : undefined,
    });
    makeAutoObservable(this);
  }

  checkFutureTransaction() {
    if (this.combinedDateAndTime && isAfter(this.combinedDateAndTime, new Date())) {
      this.warning = { key: "transactions.warning-future-transaction" };
    } else {
      this.warning = undefined;
    }
  }

  setAndCommitTransactionDate(value: string) {
    this.setTransactionDate(value);
    this.transactionDate.commit();
  }

  setTransactionDate(value: string) {
    const oldDate = this.transactionDate.inputValue;
    if (value !== oldDate) {
      this.transactionDate.setInputValue(value);
      this.transactionTime.setAndCommitInputValue(nextAvailableTime(value, this.minDate));
    }
  }

  get combinedDateAndTime(): string | undefined {
    if (this.transactionDate.validatedValue && this.transactionTime.validatedValue) {
      return combineDateAndTime(
        this.transactionDate.validatedValue,
        this.transactionTime.validatedValue,
      );
    }
  }

  get isValid() {
    return this.transactionDate.isValid && this.transactionTime.isValid;
  }

  get isSubmitEnabled() {
    return Boolean(this.isSet);
  }

  validate() {
    this.transactionDate.validate();
    this.transactionTime.validate();
    const validatedValue = this.transactionDate.validatedValue;
    const minDateIso = toLocalDateISOString(this.minDate);
    if (validatedValue === minDateIso) {
      const time = toLocalTime(this.minDate);
      this.transactionTime.validateAfterTime(time);
    }
    if (this.maxDate) {
      const maxDateIso = toLocalDateISOString(this.maxDate);
      if (validatedValue === maxDateIso) {
        const maxTime = toLocalTime(this.maxDate);
        this.transactionTime.validateBeforeTime(maxTime);
      }
    }
    return this.isValid;
  }

  get isSet() {
    return (
      this.isValid && this.transactionDate.validatedValue && this.transactionTime.validatedValue
    );
  }
}

export class TransactionDateCardModel {
  transactionDate: LocalDateFieldModel;
  transactionTime: TimeFieldModel;
  data: { transactionLog: Transaction[] };
  info?: TMessage;
  error?: TMessage;
  warning?: TMessage;
  isPrefilled: boolean = false;
  isLastTransaction: boolean = false;
  allowAnyDate: boolean = false;

  constructor({
    data,
    onChange,
    value,
    isLastTransaction,
    allowAnyDate,
  }: {
    data: { transactionLog: Transaction[] };
    onChange?: (date?: string, time?: string) => void;
    value?: string;
    isLastTransaction?: boolean;
    allowAnyDate?: boolean;
  }) {
    this.data = data;
    this.isPrefilled = !!value;
    this.isLastTransaction = !!isLastTransaction;
    this.allowAnyDate = !!allowAnyDate;

    this.transactionTime = new TimeFieldModel({
      value: value ? toLocalTime(value) : DEFAULT_TIME,
      required: true,
      onChange: (value) => {
        if (this.validate()) {
          onChange?.(this.transactionDate.validatedValue, value);
        }
      },
    });
    this.transactionDate = new LocalDateFieldModel({
      value: value ? toLocalDateISOString(value) : undefined,
      required: true,
      onChange: (value) => {
        if (value) {
          const timesByDate = groupBy(this.transactionDates, (date) => toLocalDateISOString(date));
          const maxTimeByDate = mapValues(timesByDate, (times) => max(times));
          const maxTime = maxTimeByDate[value];
          this.transactionTime.setAndCommitInputValue(
            maxTime ? nextAvailableTime(value, maxTime) : DEFAULT_TIME,
          );
        }
      },
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get transactionDates(): string[] {
    return this.data.transactionLog.map((t) => t.transactionDate);
  }

  validateExistingTransactions() {
    const inputDate = this.combinedDateAndTime;
    if (!inputDate || this.data.transactionLog.length === 0) {
      return;
    }

    if (this.transactionDates.includes(inputDate)) {
      this.error = { key: "transactions.existing-transaction-on-date" };
    }

    if (!this.allowAnyDate) {
      const transactionDatesBefore = this.transactionDates.filter((date) => date < inputDate);
      if (transactionDatesBefore.length === 0) {
        this.error = {
          key: "transactions.error-no-transaction-before",
          params: { date: { type: "datetime", value: min(this.transactionDates)! } },
        };
        return;
      }
    }

    const transactionDatesAfter = this.transactionDates.filter((date) => date > inputDate);
    if (this.isLastTransaction && !this.allowAnyDate) {
      if (transactionDatesAfter.length > 0) {
        this.error = {
          key: "transactions.error-must-be-last",
          params: { date: { type: "datetime", value: max(this.transactionDates)! } },
        };
        return;
      }
    }

    this.info =
      transactionDatesAfter.length === 0
        ? undefined
        : {
            key: "transactions.transactions-after-found",
            params: {
              count: transactionDatesAfter.length,
              date: { type: "datetime", value: inputDate },
            },
          };

    if (isAfter(inputDate, new Date())) {
      this.warning = { key: "transactions.warning-future-transaction" };
    } else {
      this.warning = undefined;
    }
  }

  validate() {
    if (this.isPrefilled) return;
    this.error = undefined;
    this.transactionDate.validate();
    this.transactionTime.validate();
    this.validateExistingTransactions();
    return this.isValid;
  }

  get isValid(): boolean {
    return this.error === undefined && this.transactionDate.isValid && this.transactionTime.isValid;
  }

  get combinedDateAndTime(): string | undefined {
    if (this.transactionDate.validatedValue && this.transactionTime.validatedValue) {
      return combineDateAndTime(
        this.transactionDate.validatedValue,
        this.transactionTime.validatedValue,
      );
    }
  }

  get isSet() {
    return (
      this.isValid && this.transactionDate.validatedValue && this.transactionTime.validatedValue
    );
  }

  clearErrors() {
    this.transactionDate.error = undefined;
    this.transactionTime.error = undefined;
  }
}

export type NumberOfShares = {
  sharetypeCode: ShareTypeCode;
  numberOfShares: number;
};

export function totalNumberOfShares(shareholders: ExtendedShareholderDTO[]): NumberOfShares[] {
  const aggregatedCounts: { [key in ShareTypeCode]?: number } = {};
  shareholders.forEach((s) => {
    Object.entries(s.calculatedShares).forEach(([sharetypeCode, count]) => {
      if (!aggregatedCounts[sharetypeCode as ShareTypeCode]) {
        aggregatedCounts[sharetypeCode as ShareTypeCode] = 0;
      }
      aggregatedCounts[sharetypeCode as ShareTypeCode]! += count!;
    });
  });
  return Object.entries(aggregatedCounts).map(([sharetypeCode, numberOfShares]) => ({
    sharetypeCode: sharetypeCode as ShareTypeCode,
    numberOfShares,
  }));
}

export type DividendTransaction = TransactionType.PAY_DIVIDEND | TransactionType.GROUP_CONTRIBUTION;

export enum CalculationMode {
  AUTO = "AUTO",
  MANUAL = "MANUAL",
}

export enum Resolution {
  POA = "PoA",
  GENERAL_MEETING = "generalMeeting",
}

export function createResolutionMode({
  transactionType,
  onChange,
  value,
}: {
  transactionType: DividendTransaction;
  onChange: (option: Resolution | undefined) => void;
  value: Resolution;
}) {
  return new RadioFieldModel<Resolution | undefined>({
    required: true,
    value: value,
    options: [Resolution.GENERAL_MEETING, Resolution.POA],
    idFn: identity,
    labelFn: (option) =>
      option === Resolution.POA
        ? resolutionGeneralMeetingLabel[transactionType]
        : { key: "transactions.dividendTransaction.general-meeting" },
    onChange,
  });
}

const resolutionGeneralMeetingLabel: { [key in DividendTransaction]: TMessage } = {
  [TransactionType.PAY_DIVIDEND]: { key: "transactions.dividendTransaction.board-according-poa" },
  [TransactionType.GROUP_CONTRIBUTION]: {
    key: "transactions.dividendTransaction.power-of-attorney",
  },
};

export const useFilterTransactions = () => {
  const { transactionLog } = useShareholdersStore();
  const { t } = useTranslation();
  const [selectedTransactionFilter, setSelectedTransactionFilter] = useState<string>(
    FilterKeys.ALL_TRANSACTIONS,
  );

  const onFilter = (key: string) => {
    setSelectedTransactionFilter(key);
  };

  const filteredTransactionLog: Transaction[] = useMemo(
    () =>
      transactionLog.filter((transaction) =>
        filterKeysMap[selectedTransactionFilter as FilterKeys].some(
          (transactionType) => transactionType === transaction.transactionType,
        ),
      ),
    [transactionLog, selectedTransactionFilter],
  );

  type TransactionOptions = {
    value: FilterKeys;
    label: string;
  };

  const transactionOptions: TransactionOptions[] = Object.values(
    FilterKeys,
  ).map<TransactionOptions>((filterValue) => ({
    value: filterValue,
    label: t(filterKeyLabels[filterValue]),
  }));

  return {
    selectedTransactionFilter: selectedTransactionFilter,
    onFilter,
    filteredTransactionLog,
    transactionOptions,
  };
};

export function dateBefore(date: string) {
  const dateMinusOneMs = new Date(toDate(date).getTime() - 1);
  return dateMinusOneMs.toISOString();
}

export function prepTransactionContextMenuItems({
  transaction,
  uploadTransactionAttachment,
  deleteTransaction,
  changeNominalValue,
  changeTransactionDate,
  downloadContractNotes,
}: {
  transaction: Transaction;
  uploadTransactionAttachment?: (files: File[]) => void;
  deleteTransaction?: (transaction: Transaction) => void;
  changeNominalValue?: (transaction: Transaction) => void;
  changeTransactionDate?: (transaction: Transaction) => void;
  downloadContractNotes?: (transaction: Transaction) => void;
}): DropdownMenuItemType[] {
  const projectId: string | undefined = transaction.projectId;
  const isDisplayContractNotesDownloadButton =
    downloadContractNotes && transaction.transactionType === TransactionType.BUY_SELL;
  const showNominalExtras =
    changeNominalValue !== undefined && isInitialTransactionType(transaction.transactionType);
  const isEditTransactionType = supportsEdit(transaction);
  const editTransaction: DCEvent[] | undefined = isEditTransactionType
    ? [
        navigate(
          adminEditShareholdersTransaction(
            transaction.organizationnumber,
            transaction.transactionlogId,
          ),
        ),
      ]
    : undefined;

  const openInProject: DCEvent[] | undefined = projectId
    ? [
        navigate(adminProject({ orgNo: transaction.organizationnumber, projectId }), {
          newWindow: true,
        }),
      ]
    : undefined;
  const showAdditionalMenuItems = isDisplayContractNotesDownloadButton || showNominalExtras;
  return compact([
    showNominalExtras && {
      type: "item",
      label: { key: "transactions.change-nominal-value" },
      onSelect: () => changeNominalValue(transaction),
      icon: "edit",
    },
    isDisplayContractNotesDownloadButton && {
      type: "item",
      label: { key: "shareholders.downloadContractNotes" },
      onSelect: () => downloadContractNotes(transaction),
      icon: "download",
      id: "downloadContractNotes",
    },
    showAdditionalMenuItems && { type: "separator" },
    uploadTransactionAttachment &&
      transaction.fileUploadConfig && {
        type: "file-upload",
        label: { key: "upload-attachments" },
        onSelect: uploadTransactionAttachment,
        fileUploadConfig: transaction.fileUploadConfig,
      },
    changeTransactionDate && {
      type: "item",
      label: isNotRegisteredIssue(transaction)
        ? { key: "transactions.enter-registration-date" }
        : { key: "transactions.change-date" },
      onSelect: () => changeTransactionDate(transaction),
      icon: "calendar",
    },
    editTransaction && {
      type: "item",
      label: { key: "transactions.edit" },
      onSelect: editTransaction,
      icon: "edit",
    },
    openInProject && {
      type: "item",
      label: { key: "transactions.context-menu.open-in-project" },
      onSelect: openInProject,
      icon: "arrow-square-out",
    },
    deleteTransaction && { type: "separator" },
    deleteTransaction && {
      type: "item",
      label: { key: "transactions.delete" },
      onSelect: () => deleteTransaction(transaction),
      icon: "trash",
      color: "error",
    },
  ]);
}

export function findAdjacentTransactions(transactionLog: Transaction[], transactionId: string) {
  const index = transactionLog.findIndex((t) => String(t.transactionlogId) === transactionId);
  const nextTransaction = index > 0 ? transactionLog[index - 1] : undefined;
  const previousTransaction =
    index < transactionLog.length - 1 ? transactionLog[index + 1] : undefined;
  return [previousTransaction, nextTransaction];
}

export function partitionTransactionAttachments(attachments: UploadedAttachment[]): {
  documents: UploadedAttachment[];
  uploaded: UploadedAttachment[];
} {
  const uploaded = attachments.filter((a) => a.type === AttachmentType.TRANSACTIONLOG_ATTACHMENT);
  return { documents: attachments.filter((a) => !uploaded.includes(a)), uploaded };
}
